/**
 * Performance mode store — controls rendering fidelity across the application.
 *
 * Manages a master `mode` ('full' | 'performance') and eight granular category
 * toggles. The mode is auto-derived from category state: all categories true
 * means 'full', any category false means 'performance'.
 *
 * On first rehydration, two migrations run in a fixed order:
 * 1. **sidebarBehavior stripper** — removes the obsolete `sidebarBehavior` key
 *    from `categories` via direct mutation of the rehydrated state object.
 *    Must run first; if any `set()` call runs before it the reference becomes stale.
 * 2. **animation-storage migration** — reads the legacy `"animation-storage"` key
 *    and, if `animationsEnabled` was false, calls `setCategory('animations', false)`,
 *    then removes the old key from localStorage.
 *
 * **Persistence:** localStorage (key: `"performance-storage"`)
 *
 * **Key exports:** `mode`, `categories`, `applyReducedMotionSuggestion`,
 * `toggleCategory`, `setCategory`, `applyPreset`, `resetToDefaults`,
 * `dismissSuggestion`
 */
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { PerformanceCategories, PerformanceMode, CategoryKey, PerformancePreset } from '@core/constants/types/performance/performance';
import deriveMode from '@core/lib/helpers/derive-performance-mode';

interface PerformanceState {
  mode: PerformanceMode;
  categories: PerformanceCategories;
  suggestionDismissed: boolean;

  toggleCategory: (key: CategoryKey) => void;
  setCategory: (key: CategoryKey, value: boolean) => void;
  applyPreset: (preset: PerformancePreset) => void;
  applyReducedMotionSuggestion: () => void;
  resetToDefaults: () => void;
  dismissSuggestion: () => void;
}

const allCategoriesTrue: PerformanceCategories = {
  animations: true,
  blur: true,
  visualEffects: true,
  charts: true,
  complexLayouts: true,
  backgroundProcessing: true,
  shadows: true,
  transitions: true,
};

const allCategoriesFalse: PerformanceCategories = {
  animations: false,
  blur: false,
  visualEffects: false,
  charts: false,
  complexLayouts: false,
  backgroundProcessing: false,
  shadows: false,
  transitions: false,
};

/** Named performance presets for one-click configuration. */
export const PERFORMANCE_PRESETS: Record<PerformancePreset, PerformanceCategories> = {
  maximum: { ...allCategoriesTrue },
  high: { ...allCategoriesTrue, blur: false },
  balanced: {
    ...allCategoriesTrue,
    blur: false,
    shadows: false,
    visualEffects: false,
  },
  performance: {
    ...allCategoriesTrue,
    animations: false,
    transitions: false,
    shadows: false,
    blur: false,
    visualEffects: false,
    backgroundProcessing: false,
  },
  minimum: { ...allCategoriesFalse },
};

const initialState = {
  mode: 'full' as PerformanceMode,
  categories: { ...allCategoriesTrue },
  suggestionDismissed: false,
};


const usePerformanceStore = create<PerformanceState>()(
  persist(
    (set) => ({
      ...initialState,

      /**
       * Toggles one category and re-derives the master mode from the result.
       *
       * @param key - The category to toggle.
       */
      toggleCategory: (key: CategoryKey) =>
        set((state) => {
          const categories = { ...state.categories, [key]: !state.categories[key] };
          return { categories, mode: deriveMode(categories) };
        }),

      /**
       * Explicitly sets one category value and re-derives the master mode.
       *
       * @param key - The category to update.
       * @param value - The new boolean value.
       */
      setCategory: (key: CategoryKey, value: boolean) =>
        set((state) => {
          const categories = { ...state.categories, [key]: value };
          return { categories, mode: deriveMode(categories) };
        }),

      /**
       * Applies a named preset, replacing all category values and re-deriving
       * the master mode in one atomic update.
       *
       * @param preset - One of 'maximum' | 'high' | 'balanced' | 'performance' | 'minimum'.
       */
      applyPreset: (preset: PerformancePreset) => {
        const categories = PERFORMANCE_PRESETS[preset];
        set({ categories: { ...categories }, mode: deriveMode(categories) });
      },

      /**
       * Apply the 'performance' preset and dismiss the reduced-motion suggestion
       * banner. Convenience action used by the banner's primary CTA.
       */
      applyReducedMotionSuggestion: () => {
        const categories = PERFORMANCE_PRESETS['performance'];
        set({ categories: { ...categories }, mode: deriveMode(categories), suggestionDismissed: true });
      },

      /** Restores mode to 'full' and all categories to true. */
      resetToDefaults: () => set({ ...initialState }),

      /** Prevents the performance suggestion banner from appearing again. */
      dismissSuggestion: () => set({ suggestionDismissed: true }),
    }),
    {
      name: 'performance-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        mode: state.mode,
        categories: state.categories,
        suggestionDismissed: state.suggestionDismissed,
      }),
      onRehydrateStorage: () => (state) => {
        if (!state) return;

        // Drop the removed sidebarBehavior field if a previous version persisted it.
        // MUST run BEFORE any setCategory/set() call below, otherwise Zustand's state
        // replacement leaves the stripper operating on a stale reference.
        if (
          state.categories
          && typeof state.categories === 'object'
          && !Array.isArray(state.categories)
          && 'sidebarBehavior' in state.categories
        ) {
          const { sidebarBehavior: _, ...rest } = state.categories as Record<string, boolean>;
          state.categories = rest as unknown as PerformanceCategories;
        }

        // Legacy animation-storage migration (must run AFTER the stripper above).
        try {
          const raw = localStorage.getItem('animation-storage');
          if (raw) {
            const parsed = JSON.parse(raw) as { state?: { animationsEnabled?: boolean } };
            if (parsed?.state?.animationsEnabled === false) {
              state.setCategory('animations', false);
            }
            localStorage.removeItem('animation-storage');
          }
        } catch {
          // Malformed legacy storage — ignore and remove.
          localStorage.removeItem('animation-storage');
        }
      },
    }
  )
);

export default usePerformanceStore;
